import { MedplumClient } from "@medplum/core";

import { EncounterProceduresScope } from "@/encounters/constants/procedures";
import { toFailureDetails } from "@/utils/errors";

import {
  SaveBillerSectionProceduresFailureReason,
  SaveBillerSectionProceduresResult,
} from "../types/biller-section-procedures";
import { StagedProceduresOperation } from "../types/procedures";

import { captureEncounterChargeItems } from "./encounter-charge-items";
import {
  getEncounterProceduresAndConditions,
  saveEncounterProceduresAndConditions,
} from "./procedures";

type SaveBillerSectionProceduresParams = {
  medplum: MedplumClient;
  clientId: string;
  encounterId: string;
  /** One session the biller finished in the picker, held by the tab until Save Billing Info. */
  operation: StagedProceduresOperation;
  isBiller: boolean;
  /**
   * The practice whose fee definition the charges are priced against, passed in for the same reason
   * the capture service takes it: it belongs to the signed-in session rather than to this write.
   */
  practiceOrganizationId: string | undefined;
};

const fail = (
  reason: SaveBillerSectionProceduresFailureReason,
  details?: string,
): SaveBillerSectionProceduresResult => ({ ok: false, reason, ...(details ? { details } : {}) });

/**
 * Writes one staged biller-section entry: the diagnosis and procedures the biller picked, and the
 * charge that makes each procedure billable.
 *
 * Everything it writes belongs to the biller section rather than the encounter's clinical record —
 * `EncounterProceduresScope.NonClinical` both marks the resources and scopes what the write
 * reconciles against, so a code the clinician already recorded is left alone and the biller's own
 * entry is created beside it.
 *
 * Charging is a second step because a ChargeItem points at a Procedure that has to exist first. It
 * runs over everything the biller section holds rather than only what this entry added: charges
 * already captured are skipped, so an entry whose charges failed the first time is picked up by the
 * next save instead of staying unbillable.
 *
 * Safe to run twice over the same entry. The write matches the resources it made last time by code
 * and rewrites them in place, and every charge is created conditionally on its own capture key, so a
 * retry after a failure part-way through adds what is missing and duplicates nothing.
 */
export const saveBillerSectionProcedures = async ({
  medplum,
  clientId,
  encounterId,
  operation,
  isBiller,
  practiceOrganizationId,
}: SaveBillerSectionProceduresParams): Promise<SaveBillerSectionProceduresResult> => {
  if (!clientId.trim()) return fail("missing_client_id");
  if (!encounterId.trim()) return fail("missing_encounter_id");

  try {
    await saveEncounterProceduresAndConditions({
      medplum,
      clientId,
      encounterId,
      isBiller,
      scope: EncounterProceduresScope.NonClinical,
      formValues: operation.formValues,
      initialValues: operation.initialValues,
    });
  } catch (error) {
    return fail("write_failed", toFailureDetails(error));
  }

  let readResult;
  try {
    // Read back rather than derived from the bundle: the procedures come back with the ids their
    // charges have to point at, and a code the biller had already entered resolves to the one
    // resource that exists rather than to a second copy of it.
    readResult = await getEncounterProceduresAndConditions({
      medplum,
      clientId,
      encounterId,
      scope: EncounterProceduresScope.NonClinical,
    });
  } catch (error) {
    return fail("procedures_read_failed", toFailureDetails(error));
  }
  const { procedures } = readResult;

  let capture;
  try {
    capture = await captureEncounterChargeItems({
      medplum,
      clientId,
      encounterId,
      procedures,
      practiceOrganizationId,
    });
  } catch (error) {
    return fail("charge_capture_failed", toFailureDetails(error));
  }

  // Only a capture that wrote its charges can also have failed to move their Location.
  const placeOfServiceRefreshFailure =
    capture.ok || capture.reason === "accounts_failed"
      ? capture.placeOfServiceRefreshFailure
      : undefined;
  const refreshFailureField = placeOfServiceRefreshFailure ? { placeOfServiceRefreshFailure } : {};

  if (!capture.ok) {
    // The capture's own reason is carried through: which read or write failed is what tells ops
    // whether this was the encounter, the payer, or the charges themselves.
    return {
      ...fail(
        "charge_capture_failed",
        capture.details ? `${capture.reason}: ${capture.details}` : capture.reason,
      ),
      ...refreshFailureField,
    };
  }

  return { ok: true, data: { chargeItems: capture.data }, ...refreshFailureField };
};
